import React from 'react';
import { Plus, Trash2, Star } from 'lucide-react';

/**
 * EmailListEditor — ריבוי כתובות מייל לאיש קשר עם דיפולט חובה.
 *
 * מודל הנתונים: `email` (הדיפולט — אליו נשלח כל דיוור/קבלה) +
 * `additionalEmails` (שאר הכתובות). הרכיב עובד על מערך אחיד של שורות
 * שבו בדיוק שורה אחת מסומנת ככוכב (דיפולט) — אי אפשר לבטל את הדיפולט,
 * רק להעביר אותו לשורה אחרת, ולכן "חובה להגדיר דיפולט" נאכף מבנית.
 */

export interface EmailRow {
  address: string;
  isDefault: boolean;
}

export function rowsFromContact(email?: string, additional?: string[]): EmailRow[] {
  const rows: EmailRow[] = [];
  rows.push({ address: email || '', isDefault: true });
  for (const a of additional || []) {
    if (a && a.trim()) rows.push({ address: a, isDefault: false });
  }
  return rows;
}

/** default → `email`, the rest (non-empty, deduped) → `additionalEmails` */
export function rowsToPayload(rows: EmailRow[]): { email: string; additionalEmails: string[] } {
  const def = rows.find((r) => r.isDefault);
  const email = (def?.address || '').trim();
  const seen = new Set<string>([email.toLowerCase()]);
  const additionalEmails: string[] = [];
  for (const r of rows) {
    const a = r.address.trim();
    if (!a || r.isDefault) continue;
    const k = a.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    additionalEmails.push(a);
  }
  return { email, additionalEmails };
}

const FIELD_CLASS = 'w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white text-sm';

const EmailListEditor: React.FC<{
  rows: EmailRow[];
  onChange: (rows: EmailRow[]) => void;
}> = ({ rows, onChange }) => {
  const setAddress = (i: number, address: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, address } : r)));

  const setDefault = (i: number) =>
    onChange(rows.map((r, j) => ({ ...r, isDefault: j === i })));

  const remove = (i: number) => {
    const next = rows.filter((_, j) => j !== i);
    if (next.length === 0) next.push({ address: '', isDefault: true });
    // the default row must always exist — deleting it crowns the first row
    if (!next.some((r) => r.isDefault)) next[0] = { ...next[0], isDefault: true };
    onChange(next);
  };

  const add = () => onChange([...rows, { address: '', isDefault: false }]);

  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDefault(i)}
            title={r.isDefault ? 'כתובת ברירת המחדל — אליה נשלח הדיוור' : 'הפוך לברירת מחדל'}
            className={`p-1.5 rounded-lg transition flex-shrink-0 ${
              r.isDefault
                ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30'
                : 'text-gray-300 hover:text-amber-400'
            }`}
          >
            <Star size={16} fill={r.isDefault ? 'currentColor' : 'none'} />
          </button>
          <input
            type="email"
            dir="ltr"
            className={FIELD_CLASS}
            placeholder="example@email.com"
            value={r.address}
            onChange={(e) => setAddress(i, e.target.value)}
          />
          {rows.length > 1 && (
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-1.5 text-gray-300 hover:text-red-500 flex-shrink-0"
              title="הסר כתובת"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          <Plus size={13} /> הוסף כתובת מייל
        </button>
        {rows.length > 1 && (
          <span className="text-[11px] text-gray-400">⭐ = ברירת המחדל שמקבלת את הדיוור</span>
        )}
      </div>
    </div>
  );
};

export default EmailListEditor;
